import { Injectable, NgZone, OnDestroy } from "@angular/core";
import { Router, NavigationExtras } from "@angular/router";
import { Subscription, Subject, of, throwError } from "rxjs";
import {
  takeUntil,
  take,
  timeout,
  catchError,
  skip,
  filter,
  map,
} from "rxjs/operators";

import { AlertController } from "@ionic/angular";
import { FirestoreService } from "../firestore.service";
import { DeviceService } from "@state/device/device.service";
import { DeviceQuery } from "@state/device/device.query";

import { ICurrentDevice } from "@interfaces/device.interface";
import { ToastService } from "../toast.service";
import { AlarmService } from "@shared/state/alarm/alarm.service";
import { AlarmQuery } from "@shared/state/alarm/alarm.query";
import { TranslateService } from "@ngx-translate/core";
import { IAlarm } from "@shared/interfaces/alarm.interface";

import { RemoteUserService } from "@shared/state/remote-user/remote-user.service";
import { UserQuery } from "@shared/state/user/user.query";
import { ICurrentUser } from "@shared/interfaces/user.interface";
import { UserService } from "@shared/state/user/user.service";
import { FirmwareQuery } from "@shared/state/firmware/firmware.query";
import { WorkerMessage } from "@shared/worker/models/worker-message.model";
import { WorkerService } from "@shared/worker/worker.service";
import { WORKER_TOPIC } from "@shared/worker/models/worker-topic.constants";

import { IonLoaderService } from "@shared/services/loader.service";
import { ConnectionStateService } from "@shared/services/connection-state.service";
/**********************encryption************************* */

import { BleSecureService } from "@shared/services/ble-secure.service";

@Injectable({
  providedIn: "root",
})
export class BleWebService implements OnDestroy {
  public gattService!: BluetoothRemoteGATTService[];
  public characteristics: BluetoothRemoteGATTCharacteristic[] = [];
  public gattServer!: BluetoothRemoteGATTServer;
  public deviceValueSubscription!: Subscription;
  private alarmSubscription!: Subscription;
  public bluetoothDevice!: BluetoothDevice;
  public disconnectedFromUser = false;
  public defaultMainTab = "Users";

  public systemType$ = this.deviceQuery.systemType$;

  private destroy$ = new Subject<void>();
  private readonly APPROVAL_TIMEOUT = 30000; // 30 seconds
  private readonly NAVIGATION_DELAY = 500;
  private currentUser: ICurrentUser | null = null;

  private approved$ = this.connectionState.approved$;
  private approvalSubscription: Subscription | null = null;
  private approvalAlertShown = false;
  private currentAlert: HTMLIonAlertElement | null = null;

  constructor(
    private firestoreService: FirestoreService,
    private deviceService: DeviceService,

    private deviceQuery: DeviceQuery,
    private router: Router,
    private ngZone: NgZone,
    private toastService: ToastService,
    private alarmService: AlarmService,
    private remoteUserService: RemoteUserService,
    private userQuery: UserQuery,
    private userService: UserService,
    private firmwareQuery: FirmwareQuery,
    private workerService: WorkerService,
    private alarmQuery: AlarmQuery,
    private translateService: TranslateService,
    private alertController: AlertController,
    private secureSvc: BleSecureService,

    private ionLoaderService: IonLoaderService,
    private connectionState: ConnectionStateService,
  ) {}

  public subscribeToAlarms() {
    this.alarmSubscription?.unsubscribe();
    this.alarmSubscription = this.alarmQuery.alarms$.subscribe((alarms) => {
      if (alarms.length > 0) {
        alarms.forEach((alarm) => {
          if (alarm && alarm.isOldAlarm === false) {
            // if (!this.firmwareQuery.ongoingUpgrade && !this.firmwareQuery.keepGoing) {
            //     this.showNewAlarm(alarm);
            // }

            this.alarmService.setAlarmToOld(alarm);
          }
        });
      }
    });
  }

  private prepareDisconnect(address: string) {
    this.secureSvc.disable();
    this.deviceService.updateLastConnected();
    this.deviceService.removeCurrentDevice();

    this.deviceService.stopConnection();
    this.alarmSubscription?.unsubscribe();

    this.deviceService.resetDeviceData();

    this.userService.updateLatestConnectionInfo();

    const stopSubscription = new WorkerMessage(
      WORKER_TOPIC.stopSubscriptions,
      "",
    );
    const clearWorkerState = new WorkerMessage(
      WORKER_TOPIC.clearWorkerState,
      "",
    );
    this.workerService.doWork(stopSubscription);
    this.workerService.doWork(clearWorkerState);

    this.router.navigate(["/reload"], { replaceUrl: true });
    this.alarmService.resetAlarmData();
    this.firestoreService.activateDuplicateFlag();
  }

  public disconnect(address: string) {
    this.disconnectedFromUser = true;
    this.gattServer.disconnect();
    this.prepareDisconnect(address);
  }

  public async showNewAlarm(alarm: IAlarm): Promise<void> {
    //console.log(alarm);

    const dismiss = this.translateService.instant("alerts.dismiss");
    const later = this.translateService.instant("alerts.later");
    const alert = await this.alertController.create({
      header: "New Alarm",
      subHeader:
        "Name: " +
        alarm.Name +
        ", ECU ID: " +
        alarm.ecuId +
        ", Error Code ID: " +
        alarm.errorCodeId,
      message: alarm.comment,
      cssClass: "alert-wrapper",
      keyboardClose: false,
      backdropDismiss: false,
      buttons: alarm.alarmInfoList.AckAlarm
        ? [
            {
              text: dismiss,
              role: "dismiss",
            },
            {
              text: later,
              role: "later",
            },
          ]
        : [
            {
              text: "OK",
              role: "ok",
            },
          ],
    });
    alert.present();
    alert.onDidDismiss().then(async (res) => {
      switch (res.role) {
        case "dismiss":
          this.alarmService.removeAlarm(alarm);
          break;

        case "later":
          this.toastService.dismisstoast();
          this.toastService.infoToast("toasts.alarm dismiss later", true);
          break;

        case "ok":
          this.toastService.dismisstoast();
          break;

        default:
          break;
      }
    });
  }

  private delayPromise(delay: number) {
    return new Promise((resolve) => {
      setTimeout(resolve, delay);
    });
  }

  public connectToDeviceWeb(server: BluetoothRemoteGATTServer) {
    this.secureSvc.disable();
    this.deviceService.resetDeviceData();
    this.alarmService.resetAlarmData();
    this.subscribeToAlarms();
    this.deviceService.startConnection();

    setTimeout(() => {
      this.gattServer = server;
      server.device.addEventListener("gattserverdisconnected", (event) =>
        this.handleDisconnect(event),
      );
      this.startSubscribeConnectionWeb(server);
    }, 250);
  }

  private handleDisconnect($event: Event) {
    // console.log("handleDisconnect");

    const isFirmwareUpgrade = this.firmwareQuery.ongoingUpgrade === true;
    const target = $event.target;

    if (!this.disconnectedFromUser) {
      this.remoteUserService.resetStore();
      this.deviceService.resetDeviceData();
      this.alarmService.resetAlarmData();

      this.defaultMainTab = "Users";
      this.deviceValueSubscription?.unsubscribe();
      if (target && this.isBluetoothDevice(target)) {
        this.bluetoothDevice = target;
      }

      if (!isFirmwareUpgrade) {
        this.router.navigate(["/start"]);
        this.bluetoothDevice.gatt?.disconnect();
      }
    }

    // Always cleanup
    this.firestoreService.cancelFirestoreLink();
    this.characteristics = [];
    this.userService.serviceToolBleIsActive(false);
    this.deviceService.stopConnection();

    // Only reconnect if firmware upgrade is ongoing AND user didn't disconnect
    if (isFirmwareUpgrade && !this.disconnectedFromUser) {
      this.connectAfterDisconnect();
    }
  }

  private isBluetoothDevice(value: unknown): value is BluetoothDevice {
    return typeof value === "object" && value !== null && "gatt" in value;
  }
  private async connectAfterDisconnect(attempt: number = 0) {
    const MAX_ATTEMPTS = 5;
    await this.delay(5000);

    if (this.disconnectedFromUser) return;
    if (!this.firmwareQuery.ongoingUpgrade) return;

    const device = this.bluetoothDevice;
    if (!device) {
      // We can't reconnect without a reference
      this.router.navigate(["/start"]);
      return;
    }

 const gatt = device.gatt;
  if (!gatt) {
    this.router.navigate(['/start']);
    return;
  }

  try {
    if (!gatt.connected) {
      await gatt.connect();
    }

    this.startSubscribeConnectionWeb(gatt);
    return; // ✅ success: stop retrying
  } catch (error) {
    console.log(`Reconnect attempt ${attempt + 1} failed:`, error);

    if (attempt + 1 >= MAX_ATTEMPTS) {
      this.router.navigate(['/start']);
      return;
    }

    // ✅ retry
    await this.connectAfterDisconnect(attempt + 1);
  }

    
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  public async startSubscribeConnectionWeb(
    server: BluetoothRemoteGATTServer,
  ): Promise<void> {
    this.disconnectedFromUser = false;
    var gattService: BluetoothRemoteGATTService[] = [];
    var characteristics: BluetoothRemoteGATTCharacteristic[] = [];
    var connectionApproved = server.device?.gatt?.connected ? true : false;
    //  console.log("connectionApproved", connectionApproved);
    if (connectionApproved) {
      server
        .connect()
        .then((deviceStatus) => {
          if (deviceStatus.connected) {
            //   console.log("CONNECTED");

            deviceStatus
              .getPrimaryService("2456e1b9-26e2-8f83-e744-f34f01e9d701")
              .then((service) => {
                gattService.push(service);
                //console.log(service);
                service
                  .getCharacteristic("2456e1b9-26e2-8f83-e744-f34f01e9d703")
                  .then((character) => {
                    characteristics.push(character);
                    this.gattService = gattService;
                    this.characteristics = characteristics;

                    const newDevice: ICurrentDevice = {
                      address: deviceStatus.device.id.replaceAll("/", ""),
                      name: deviceStatus.device.name,
                      status: "waiting for serialnumber",
                      services: "2456e1b9-26e2-8f83-e744-f34f01e9d701",
                      webServices: "2456e1b9-26e2-8f83-e744-f34f01e9d701",
                      currentConnectedBluetoothChip:
                        deviceStatus.device.id.replaceAll("/", ""),
                      bluetoothConnectors: [
                        deviceStatus.device.id.replaceAll("/", ""),
                      ],
                    };

                    this.ngZone.run(() => {
                      this.currentUser = {
                        ...this.userQuery.currentUser,
                        uid: this.userQuery.userUid,
                      };

                      this.deviceService.addCurrentDevice(newDevice);
                      this.deviceService.stopConnection();
                      this.remoteUserService.initializeServiceToolBleConnection(
                        this.currentUser,
                      );
                      this.userService.serviceToolBleIsActive(true);

                      this.handleDeviceApproval(this.currentUser);
                      /*
                      if (!this.firmwareQuery.ongoingUpgrade ) {
                        // this.ionLoaderService.simpleLoader();
                         this.approved$.subscribe((res) => {
                          if (res === true){
                             setTimeout(() => {
                          this.systemType$?.subscribe((item) => {

                            if (item !== 'TCS') {
                              this.router.navigate(['system/', item, this.currentUser.uid, 'service']);
                              if (item === 'MIC 4.0') {
                                this.ionLoaderService.dismissLoader();
                              }
                              //this.ionLoaderService.dismissLoader();
                            } else if (item === 'TCS') {
                              this.router.navigate(['system/', item, this.currentUser.uid, 'TcsDashboard']);
                              // this.ionLoaderService.dismissLoader();
                            }
                          });
                        }, 800);

                          }else {

                             setTimeout(() => {
                              
                               this.router.navigate(['']);
                              
                          }, 10000);


                          }

                         })
                       
                      }
*/
                    });
                  })
                  .catch((error) => {
                    console.error(error);
                    this.deviceService.stopConnection();
                  });
                // return service.getCharacteristic('2456e1b9-26e2-8f83-e744-f34f01e9d703'); //2456e1b9-26e2-8f83-e744-f34f01e9d704
              })
              .catch((err) => {
                console.error(err);
                this.deviceService.stopConnection();
              });
          } else if (!deviceStatus.connected) {
            this.deviceService.stopConnection();
            // console.log("deviceStatus.connected", deviceStatus.connected);
            setTimeout(() => {
              this.startSubscribeConnectionWeb(deviceStatus);
            }, 7000);
          }
        })
        .catch((err) => {
          console.log(err, "ERROR");
        });
    } else {
      this.deviceService.stopConnection();
    }
  }

  /************************************************************************* */
  private handleDeviceApproval(currentUser: ICurrentUser): void {
    if (this.firmwareQuery.ongoingUpgrade) {
      return;
    }

    // Set up timeout - if no response in 30 seconds, stop everything
    const timeoutId = setTimeout(() => {
      this.handleNoResponse();
    }, this.APPROVAL_TIMEOUT);

    // Subscribe to approval response
    this.approvalSubscription = this.approved$
      .pipe(
        skip(1),
        take(1), // Only take the first emission
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (approved) => {
          clearTimeout(timeoutId); // Clear timeout since we got a response

          if (approved === true) {
            this.handleApprovedConnection(currentUser);
          } else if (approved === false) {
            this.handleRejectedConnection();
          }
          // If approved is null/undefined, it means no response yet (shouldn't happen with take(1))
        },
        error: (error) => {
          clearTimeout(timeoutId);
          console.error("Error while waiting for approval:", error);
          this.handleConnectionError();
        },
      });
  }

  private handleApprovedConnection(currentUser: any): void {
    this.approvalAlertShown = false;
    this.dismissAnyAlerts();
    this.ionLoaderService.showLoaderWithMessage("Loading...");
    setTimeout(() => {
      this.systemType$
        .pipe(
          filter(
            (t): t is string => typeof t === "string" && t.trim().length > 0,
          ),
          map((t) => t.trim()),
          take(1),
        )
        .subscribe((systemType) => {
          //  console.log('[navigate] systemType after filter/trim =', systemType);

          this.navigateToSystem(systemType, currentUser.uid);
        });
    }, this.NAVIGATION_DELAY);
  }

  private async navigateToSystem(
    systemType: string,
    userId: string,
  ): Promise<void> {
    if (systemType === "TCS") {
      this.router.navigate(["system", systemType, userId, "TcsDashboard"], {});
      this.ionLoaderService.dismissLoader();
    } else {
      this.router.navigate(["system", systemType, userId, "service"], {});
    }
  }

  private handleRejectedConnection(): void {
    this.ionLoaderService.dismissLoader();
    this.cleanupApproval();

    this.showRejectionAlert();
  }

  private handleNoResponse(): void {
    this.cleanupApproval();
    const currentDevice =
      this.deviceQuery.currentDevice?.currentConnectedBluetoothChip;
    if (currentDevice) {
      this.disconnect(currentDevice);
    }
    this.showTimeoutAlert();
    this.ionLoaderService.dismissLoader();
  }

  public handleDisconnectForSparePart(): void {
    this.cleanupApproval();
    const currentDevice =
      this.deviceQuery.currentDevice?.currentConnectedBluetoothChip;
    if (currentDevice) {
      this.disconnect(currentDevice);
    }
  }

  private async showRejectionAlert(): Promise<void> {
    const header = await this.translateService.instant(
      `alerts.showRejectionAlert.header`,
    );
    const message = await this.translateService.instant(
      `alerts.showRejectionAlert.message`,
    );
    const buttonText = await this.translateService.instant(`auth.ok`);

    const body = `<div class="text-center">
      <p class="text-22pt text-roto-green py-2 font-semibold">${header}</p>
      <p class=" text-20pt text-roto-green">${message}</p>
    </div>`;

    const alert = await this.alertController.create({
      message: body,
      keyboardClose: false,
      buttons: [
        {
          text: buttonText,
          cssClass: "no-button-borders",
          handler: () => {
            const chip =
              this.deviceQuery.currentDevice?.currentConnectedBluetoothChip;
            if (!chip) {
              // nothing to disconnect from (or state already cleared)
              this.router.navigate([""]);
              return;
            }
            this.disconnect(chip);
            this.router.navigate([""]);
          },
        },
      ],
    });

    await alert.present();
  }

  private handleConnectionError(): void {
    this.approvalAlertShown = false;
    this.dismissAnyAlerts();
    this.ionLoaderService.dismissLoader();
    this.deviceService.stopConnection();
    this.router.navigate([""]);
  }

  private async dismissAnyAlerts(): Promise<void> {
    if (this.currentAlert) {
      await this.currentAlert.dismiss();
      this.currentAlert = null;
    }
    this.approvalAlertShown = false;
  }

  private async showTimeoutAlert(): Promise<void> {
    const header = await this.translateService.instant(
      `alerts.timeOutAlert.header`,
    );
    const message = await this.translateService.instant(
      `alerts.timeOutAlert.message`,
    );
    const buttonText = await this.translateService.instant(`auth.ok`);

    const body = `<div class="text-center">
      <p class="text-22pt text-roto-green py-2 font-semibold">${header}</p>
      <p class=" text-20pt text-roto-green">${message}</p>
    </div>`;

    const alert = await this.alertController.create({
      message: body,
      keyboardClose: false,
      buttons: [
        {
          text: buttonText,
          cssClass: "no-button-borders",

          handler: () => {
            this.router.navigate([""]);
          },
        },
      ],
    });

    await alert.present();
  }

  private cleanupApproval(): void {
    if (this.approvalSubscription) {
      this.approvalSubscription.unsubscribe();
      this.approvalSubscription = null;
    }
  }

  /************************************************************************************* */
  public async createWaitForResponseAlert(): Promise<boolean> {
    this.ionLoaderService.dismissLoader();

    const header = this.translateService.instant(
      `alerts.remote-support.tilt connection`,
    );
    const message = this.translateService.instant(
      `alerts.remote-support.waiting for the approval`,
    );
    const buttonText = this.translateService.instant(`pendingauth.cancel`);

    const body = `
    <div class="text-center">
      <div class="m-auto w-1/5 flex">
        <img src="/assets/icon/Remote_Service_Cyan.png" />
      </div>
      <p class="text-26pt text-roto-green py-2 font-semibold">${header}</p>
      <p class="text-20pt text-roto-green">${message}</p>
    </div>`;

    const alert = await this.alertController.create({
      message: body,
      keyboardClose: false,
      backdropDismiss: false,
      id: "responseConnectionAlert",
      cssClass: "custom-alert-html",
      buttons: [
        {
          text: buttonText,
          role: "cancel",
          cssClass: "no-button-borders",
        },
      ],
    });

    await alert.present();
    const res = await alert.onDidDismiss();

    if (res.role === "cancel") {
      const chip =
        this.deviceQuery.currentDevice?.currentConnectedBluetoothChip;
      if (!chip) {
        // nothing to disconnect from (or state already cleared)

        return false;
      }
      this.disconnect(chip);
      return false;
    }

    return true;
  }

  public async resetBluetoothSession() {
    // console.log('resetBluetoothSession is called');
    this.characteristics?.forEach((char, i) => {
      try {
        char.stopNotifications();
      } catch {}
    });

    this.characteristics = [];
    try {
      if (this.gattServer?.connected) {
        this.gattServer.disconnect();
      }
    } catch {}

    this.deviceService.removeCurrentDevice();
  }

  ngOnDestroy(): void {
    this.cleanupApproval();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
